import type { GuideDistance } from "../render/overlayChannel";

export interface Box {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface SpacingSnap {
  /** Correction to apply along the axis. */
  offset: number;
  /** The equal gaps, for the overlay. */
  distances: GuideDistance[];
}

interface Span {
  lo: number;
  hi: number;
  crossLo: number;
  crossHi: number;
}

const project = (box: Box, axis: "x" | "y"): Span =>
  axis === "x"
    ? { lo: box.left, hi: box.right, crossLo: box.top, crossHi: box.bottom }
    : { lo: box.top, hi: box.bottom, crossLo: box.left, crossHi: box.right };

const overlaps = (a: Span, b: Span): boolean =>
  a.crossHi > b.crossLo && a.crossLo < b.crossHi;

const centre = (span: Span): number => (span.lo + span.hi) / 2;

const gapLine = (
  from: Span,
  to: Span,
  axis: "x" | "y",
  value: number
): GuideDistance => {
  // Through the middle of the overlap, so the line stays inside both.
  const cross =
    (Math.max(from.crossLo, to.crossLo) + Math.min(from.crossHi, to.crossHi)) /
    2;
  return axis === "x"
    ? {
        from: { x: from.hi, y: cross },
        to: { x: to.lo, y: cross },
        value,
        axis: "horizontal"
      }
    : {
        from: { x: cross, y: from.hi },
        to: { x: cross, y: to.lo },
        value,
        axis: "vertical"
      };
};

/**
 * Equal-spacing snap along one axis: the moving box lands where its gap to
 * the neighbour on one side equals the gap on the other side, or equals
 * the gap that neighbour already keeps to its own next neighbour. Only
 * shapes that overlap the moving box across the axis count as a row.
 */
export const snapEqualSpacing = (
  moving: Box,
  neighbours: Box[],
  axis: "x" | "y",
  threshold: number
): SpacingSnap | null => {
  const m = project(moving, axis);
  const width = m.hi - m.lo;
  const row = neighbours
    .map((box) => project(box, axis))
    .filter((span) => overlaps(span, m));
  const mCentre = centre(m);
  const before = row.filter((span) => centre(span) < mCentre);
  const after = row.filter((span) => centre(span) > mCentre);
  const left = before.length
    ? before.reduce((best, span) => (span.hi > best.hi ? span : best))
    : null;
  const right = after.length
    ? after.reduce((best, span) => (span.lo < best.lo ? span : best))
    : null;

  const options: { targetLo: number; distances: GuideDistance[] }[] = [];
  if (left && right) {
    const gap = (right.lo - left.hi - width) / 2;
    if (gap >= 0) {
      const targetLo = left.hi + gap;
      const placed = { ...m, lo: targetLo, hi: targetLo + width };
      options.push({
        targetLo,
        distances: [
          gapLine(left, placed, axis, gap),
          gapLine(placed, right, axis, gap)
        ]
      });
    }
  }
  if (left) {
    const beyond = before.filter(
      (span) =>
        span !== left && centre(span) < centre(left) && overlaps(span, left)
    );
    const next = beyond.length
      ? beyond.reduce((best, span) => (span.hi > best.hi ? span : best))
      : null;
    if (next && next.hi <= left.lo) {
      const gap = left.lo - next.hi;
      const targetLo = left.hi + gap;
      const placed = { ...m, lo: targetLo, hi: targetLo + width };
      options.push({
        targetLo,
        distances: [
          gapLine(next, left, axis, gap),
          gapLine(left, placed, axis, gap)
        ]
      });
    }
  }
  if (right) {
    const beyond = after.filter(
      (span) =>
        span !== right && centre(span) > centre(right) && overlaps(span, right)
    );
    const next = beyond.length
      ? beyond.reduce((best, span) => (span.lo < best.lo ? span : best))
      : null;
    if (next && next.lo >= right.hi) {
      const gap = next.lo - right.hi;
      const targetLo = right.lo - gap - width;
      const placed = { ...m, lo: targetLo, hi: targetLo + width };
      options.push({
        targetLo,
        distances: [
          gapLine(placed, right, axis, gap),
          gapLine(right, next, axis, gap)
        ]
      });
    }
  }

  let best: SpacingSnap | null = null;
  let bestDiff = threshold;
  for (const option of options) {
    const diff = Math.abs(option.targetLo - m.lo);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = { offset: option.targetLo - m.lo, distances: option.distances };
    }
  }
  return best;
};
